import { useCallback, useEffect, useMemo, useState } from "react";
import { AppNavigation, CWGBranding, UserInfo } from "components";
import CompressedNavigation from "components/AppNavigation/CompressedNavigation";
import { BrandingWrapper, CloseMenuIcon, NavWrapper, SidebarContainer, SidebarMenuIcon } from "./sidebar.styles";
import { useLocation, useNavigate, useParams } from "react-router";
import { getNavigationLink } from "components/AppNavigation/__helpers__/navigationHelper";
import { CabinetworksGroupApplication, UserRole } from "types/declarations/enums.ts";
import { ReplacementCartItem } from "features/reducers/replacementOrder/cart.ts";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "stores/application.store";
import { LinkNavigation, newOrderActions } from "features/reducers/newOrder/newOrder";
import useSidebar from "hooks/useSidebar";
import { models } from "types/api/viewModels.ts";
import { useUserLocation } from "hooks/useUserLocation.ts";
import { useDialog } from "hooks/useDialog.ts";
import { DialogTracking } from "components/Dialog/components/DialogTracking.ts";
import useB2CAccount from "auth/useB2CAccount.tsx";

const NewOrderNavLink: models["UserNavigationItemViewModel"] = {
	id: "",
	title: "New Order",
	link: "new-order",
	appLink: CabinetworksGroupApplication.Tandem,
	hasNestedNavigation: false,
	navigationItems: []
};

const normalizeNavigationPath = (path?: string | null) => {
	if (!path) return "";

	return path.startsWith("/") ? path : `/${path}`;
};

const pathMatchesNavigationLink = (pathname: string, link?: string | null) => {
	const normalizedLink = normalizeNavigationPath(link);
	if (!normalizedLink) return false;

	return pathname === normalizedLink || pathname.startsWith(`${normalizedLink}/`);
};

interface SidebarProps {
	userDetails: models["UserViewModel"] | undefined;
}

const Sidebar = ({ userDetails }: SidebarProps) => {
	const ordersTab = "nav-orders";
	const navigateTo = useNavigate();
	const dispatch = useDispatch();
	const { quoteId } = useParams();
	const { logout } = useB2CAccount();
	const {
		isAddOnPage,
		isAddOnConfirmationPage,
		isNewOrderPage,
		isNewOrderConfirmationPage,
		isReplacementsPage,
		isConfirmationPage,
		isTrucksPage,
		isCustomQuotesAddQuotePage,
		isCustomQuotesCreateQuotePage
	} = useUserLocation();
	const { openDialog } = useDialog();
	const location = useLocation();
	const [currentPath, setCurrentPath] = useState("");
	const cartItems: ReplacementCartItem[] = useSelector((state: RootState) => state.cart.replacements);
	const deferredNavigationState: LinkNavigation | undefined = useSelector(
		(state: RootState) => state.newOrder.leaveNewOrderFlow
	);
	const settingsNav = useMemo(
		() => ({
			id: "nav-user-info",
			title: `${userDetails?.firstName} ${userDetails?.lastName}`,
			link: null,
			icon: "DashboardNavigationLogo.svg",
			hasNestedNavigation: true,
			selectedIcon: "SelectedDashboardNavigationLogo.svg",
			navigationItems: [
				{
					id: "nav-user-info-settings",
					title: "Settings",
					link: "/user-settings",
					icon: null,
					selectedIcon: null,
					navigationItems: []
				},
				{
					id: "nav-user-info-logout",
					title: "Logout",
					link: null,
					icon: null,
					selectedIcon: null,
					navigationItems: []
				}
			]
		}),
		[userDetails?.firstName, userDetails?.lastName]
	);
	const navsWithUserSettings = useMemo(
		() => [...(userDetails?.navigation?.navigationItems ?? []), settingsNav],
		[settingsNav, userDetails?.navigation?.navigationItems]
	);
	const filteredNavItems: models["UserNavigationItemViewModel"][] = useMemo(
		() =>
			navsWithUserSettings?.filter(
				(item: models["UserNavigationItemViewModel"]) => item.id !== "nav-user-info"
			) ?? [],
		[navsWithUserSettings]
	);
	const normalizedPathname = useMemo(() => normalizeNavigationPath(location.pathname), [location.pathname]);
	const activeNavigation = useMemo(() => {
		for (const [index, navItem] of filteredNavItems.entries()) {
			if (
				navItem.navigationItems?.some((subNavItem) =>
					pathMatchesNavigationLink(normalizedPathname, subNavItem.link)
				)
			) {
				return { index, navItem };
			}

			if (!navItem.hasNestedNavigation && pathMatchesNavigationLink(normalizedPathname, navItem.link)) {
				return { index, navItem };
			}
		}

		return undefined;
	}, [filteredNavItems, normalizedPathname]);
	const ordersNavigation = useMemo(() => {
		const index = filteredNavItems.findIndex(
			(item: models["UserNavigationItemViewModel"]) => item.id === ordersTab
		);

		return index >= 0 ? { index, navItem: filteredNavItems[index]! } : undefined;
	}, [filteredNavItems, ordersTab]);
	const defaultNavigation = activeNavigation ?? ordersNavigation;

	const [leavePageModalOpen, setLeavePageModalOpen] = useState(false);
	const [subMenuOpen, setSubMenuOpen] = useState(!!defaultNavigation?.navItem.hasNestedNavigation);
	const [selectedIndex, setSelectedIndex] = useState<number | undefined>(defaultNavigation?.index);
	const [selectedMainNavItem, setSelectedMainNavItem] = useState<models["UserNavigationItemViewModel"] | null>(
		defaultNavigation?.navItem ?? null
	);

	const userInfoNavItem = () => {
		const navItem: models["UserNavigationItemViewModel"] | undefined = navsWithUserSettings?.find(
			(item: models["UserNavigationItemViewModel"]) => item.id === "nav-user-info"
		);
		if (navItem) {
			return {
				...navItem,
				title: `${userDetails?.firstName} ${userDetails?.lastName}`
			};
		}

		return navItem;
	};

	const { isSidebarOpen, openNav, closeNav } = useSidebar();

	const handleMVPButtonClick = useCallback(() => {
		if (
			((isNewOrderPage && !isNewOrderConfirmationPage) || (isAddOnPage && !isAddOnConfirmationPage)) &&
			deferredNavigationState?.state !== "pause"
		) {
			dispatch(newOrderActions.leaveNewOrderFlow({ state: "pause", navItem: NewOrderNavLink, index: 0 }));
			closeNav();
		} else {
			closeNav();
			dispatch(newOrderActions.clearNewOrder());
			navigateTo("new-order");
		}
	}, [
		closeNav,
		deferredNavigationState?.state,
		dispatch,
		isAddOnConfirmationPage,
		isAddOnPage,
		isNewOrderConfirmationPage,
		isNewOrderPage,
		navigateTo
	]);

	const handleLinkNavigation = useCallback(
		(navItem: models["UserNavigationItemViewModel"], index: number) => {
			if (navItem.id === "nav-orders-place-order") {
				handleMVPButtonClick();
			}

			// allow navigation
			if (navItem.hasNestedNavigation) {
				setSubMenuOpen(true);
				setSelectedIndex(index);
				setSelectedMainNavItem(navItem);
			} else {
				const url = getNavigationLink(navItem.hasNestedNavigation, navItem.appLink, navItem.link);

				if (leavePageModalOpen) {
					return;
				}
				setCurrentPath(url);
				closeNav();

				if (cartItems && cartItems.length > 0 && isReplacementsPage && !isConfirmationPage) {
					setLeavePageModalOpen(true);
				} else if (navItem.appLink === CabinetworksGroupApplication.Salesforce) {
					globalThis.location.href = url;
				} else {
					navigateTo(url);
				}
			}

			dispatch(newOrderActions.resetNewOrderFlow());
		},
		[
			dispatch,
			handleMVPButtonClick,
			leavePageModalOpen,
			closeNav,
			cartItems,
			isReplacementsPage,
			isConfirmationPage,
			navigateTo
		]
	);

	const [selectedNavItemLink, setSelectedNavItemLink] = useState<string | null | undefined>();

	const handleLogout = useCallback(async () => {
		if (cartItems && cartItems.length > 0 && globalThis.location.href.includes("/replacements")) {
			setLeavePageModalOpen(true);
		} else {
			await logout();
		}
	}, [cartItems, logout]);

	const handleClickNavigationOption = useCallback(
		// eslint-disable-next-line sonarjs/cognitive-complexity
		async (navItem: models["UserNavigationItemViewModel"] | undefined, index: number) => {
			if (!navItem) return;
			// Check if the user is in the new-order
			const isLogout = navItem.id === "nav-user-info-logout";
			if (!leavePageModalOpen && isLogout) {
				await handleLogout();
			}
			const isNewOrder =
				(isNewOrderPage || isAddOnPage) &&
				["build-order", "view-price-estimates", "shipping-details", "review-order"].some((path) =>
					location.pathname.includes(path)
				);
			const isTruck =
				isTrucksPage &&
				["create-truck", "add-order", "review"].some((path) => location.pathname.includes(path));
			if (
				!navItem.hasNestedNavigation &&
				(isNewOrder || isTruck || isCustomQuotesAddQuotePage || isCustomQuotesCreateQuotePage)
			) {
				if (isNewOrder) {
					dispatch(newOrderActions.leaveNewOrderFlow({ navItem, index, state: "pause" }));
					openDialog({
						dialogStatus: DialogTracking.NONE,
						dialogLeavePath: navItem.link
					});
				} else if (isTruck) {
					setSelectedNavItemLink(navItem.link);
					setLeavePageModalOpen(true);
				} else if (isCustomQuotesAddQuotePage) {
					openDialog({
						dialogStatus: DialogTracking.LEAVE_ADD_CUSTOM_QUOTE,
						dialogId: quoteId,
						dialogLeavePath: navItem.link
					});
				} else if (isCustomQuotesCreateQuotePage) {
					openDialog({
						dialogStatus: DialogTracking.LEAVE_CREATE_CUSTOM_QUOTE,
						dialogLeavePath: navItem.link
					});
				}
				closeNav();
			} else {
				const isSettingsNav = navItem.id === settingsNav.id;
				handleLinkNavigation(
					navItem.link === "/new-order" && userDetails?.role === UserRole.INTERNAL_DESIGNER
						? { ...navItem, link: "/new-quote" }
						: navItem,
					isSettingsNav ? filteredNavItems.length + 1 : index
				);
			}
		},
		[
			leavePageModalOpen,
			isNewOrderPage,
			isAddOnPage,
			isTrucksPage,
			isCustomQuotesAddQuotePage,
			isCustomQuotesCreateQuotePage,
			handleLogout,
			location.pathname,
			closeNav,
			dispatch,
			openDialog,
			quoteId,
			settingsNav.id,
			handleLinkNavigation,
			userDetails?.role,
			filteredNavItems.length
		]
	);

	const handleCloseSubMenu = () => {
		setSubMenuOpen(false);
		setSelectedIndex(globalThis.location.pathname === "/user-settings" ? undefined : defaultNavigation?.index);
		setSelectedMainNavItem(defaultNavigation?.navItem ?? null);
	};

	useEffect(() => {
		const { state, navItem, index } = deferredNavigationState || {};
		if (globalThis.location.pathname === "/user-settings") {
			handleLinkNavigation(settingsNav, filteredNavItems.length + 1);
		} else if (state === "proceed" && navItem && index !== undefined) {
			handleLinkNavigation(navItem, index);
		}
	}, [deferredNavigationState, dispatch, filteredNavItems.length, handleLinkNavigation, navigateTo, settingsNav]);

	useEffect(() => {
		setCurrentPath(location.pathname);
	}, [location.pathname]);

	useEffect(() => {
		if (!defaultNavigation || location.pathname === "/user-settings") return;

		setSubMenuOpen(!!defaultNavigation.navItem.hasNestedNavigation);
		setSelectedIndex(defaultNavigation.index);
		setSelectedMainNavItem(defaultNavigation.navItem);
	}, [defaultNavigation, location.pathname]);

	return (
		<SidebarContainer data-testid="sidebar">
			<NavWrapper
				isMainNavOpen={isSidebarOpen}
				isSubNavOpen={subMenuOpen}
			>
				<div>
					<CompressedNavigation
						handleClickNavigationOption={handleClickNavigationOption}
						handleCloseSubMenu={handleCloseSubMenu}
						mainNavigationItems={filteredNavItems}
						subNavigation={selectedMainNavItem}
						selectedIndex={selectedIndex}
						userDetails={userDetails}
					/>
				</div>
				<div>
					<BrandingWrapper>
						<CWGBranding />
						<div>
							{isSidebarOpen ? (
								<CloseMenuIcon
									data-testid="mobile-nav-close-icon"
									onClick={closeNav}
								/>
							) : (
								<SidebarMenuIcon
									data-testid="mobile-nav-menu-icon"
									onClick={openNav}
								/>
							)}
						</div>
					</BrandingWrapper>
					<div>
						<UserInfo
							data-testid="user-info"
							user={userDetails}
							handleClickNavigationOption={() => handleClickNavigationOption(userInfoNavItem() ?? {}, 0)}
						/>
					</div>
					<div data-testid="app-navigation">
						<AppNavigation
							currentPath={currentPath}
							handleClickNavigationOption={handleClickNavigationOption}
							handleCloseSubMenu={handleCloseSubMenu}
							leavePageModalOpen={leavePageModalOpen}
							mainNavigationItems={filteredNavItems}
							selectedIndex={selectedIndex}
							setLeavePageModalOpen={setLeavePageModalOpen}
							subMenuOpen={subMenuOpen}
							subNavigation={selectedMainNavItem}
							selectedNavItemLink={selectedNavItemLink}
							userDetails={userDetails}
						/>
					</div>
				</div>
			</NavWrapper>
		</SidebarContainer>
	);
};

export default Sidebar;
